/**
 * Color-scale interpolation for scatter color-by (blob
 * `colorScaleRgb`). The named stop lists live in `plot-constants.ts`
 * (`COLOR_SCALES`); this module turns a name + `t∈[0,1]` into an `rgb(...)`
 * string by linear interpolation between the bracketing stops.
 */

import { COLOR_SCALES, PLOT_COLORS, discreteScaleColormap } from '../plot-constants.ts';

export function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

export function colorScaleRgb(scaleName: string, t: number): string {
  const stops = COLOR_SCALES[scaleName] ?? COLOR_SCALES.Viridis!;
  let tt = Math.max(0, Math.min(1, t));
  let lo = stops[0]!;
  let hi = stops[stops.length - 1]!;
  for (let i = 0; i < stops.length - 1; i++) {
    if (tt >= stops[i]![0] && tt <= stops[i + 1]![0]) {
      lo = stops[i]!;
      hi = stops[i + 1]!;
      break;
    }
  }
  const f = hi[0] === lo[0] ? 0 : (tt - lo[0]) / (hi[0] - lo[0]);
  const a = parseHex(lo[1]);
  const b = parseHex(hi[1]);
  const r = Math.round(a[0] + (b[0] - a[0]) * f);
  const g = Math.round(a[1] + (b[1] - a[1]) * f);
  const bl = Math.round(a[2] + (b[2] - a[2]) * f);
  return `rgb(${r},${g},${bl})`;
}

/**
 * `n` swatch colors for a discrete (categorical) color-by scale. A bare
 * `'Discrete'` cycles the qualitative `PLOT_COLORS` palette; a
 * `'Discrete:<Colormap>'` scale samples `n` evenly-spaced colors from that
 * gradient (endpoints included) so the categories spread across the full map.
 */
export function discreteColors(scaleName: string, n: number): string[] {
  const count = Math.max(0, n);
  const map = discreteScaleColormap(scaleName);
  if (map && COLOR_SCALES[map]) {
    if (count === 0) return [];
    if (count === 1) return [colorScaleRgb(map, 0.5)];
    return Array.from({ length: count }, (_, i) => colorScaleRgb(map, i / (count - 1)));
  }
  return Array.from({ length: count }, (_, i) => PLOT_COLORS[i % PLOT_COLORS.length]!);
}
